/**
 * @voltxampere/fx — Wire glow opacity (Sprint 29-4)
 *
 * Map a branch current magnitude to a normalised glow opacity [0, 1]
 * via log-scale interpolation between a noise floor (1 µA) and a
 * saturation ceiling (1 A). Six decades of headroom matches the dynamic
 * range a typical educational circuit covers (LED leak ~ 1 µA → bridge
 * fault ~ 1 A) and gives the user perceptual proportionality:
 * each 10× increase in current adds the SAME visual brightness step.
 *
 * Sentinel rows (Sprint 29-4 frozen mappings):
 *   |I| = 0           → 0   (no current, no glow)
 *   |I| < 1 µA        → 0   (below noise floor)
 *   |I| = 1 µA        → 0   (at floor, edge case)
 *   |I| = 1 mA        → 0.5 (geometric mid of [1 µA, 1 A])
 *   |I| = 1 A         → 1   (saturation ceiling)
 *   |I| > 1 A         → 1   (clamped)
 *   |I| < 0           → mirrored (caller-side already passes |I|, but
 *                       the function is symmetric defensively)
 *
 * Renderer composition (Sprint 29-5 wires this in):
 *   stroke_opacity = 0.35 + 0.65 × glow
 *   stroke_color   = lerp(ivory, brass-hot, glow × 0.6)
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const I_MIN = 1e-6;
/** log10(I_MAX / I_MIN) = log10(1.0 / 1e-6) = log10(1e6) = 6, computed once. */
const LOG_DENOM = 6;
/**
 * Compute the log-scaled, [0, 1]-clamped glow opacity for a wire
 * carrying `current` amperes (signed input — magnitude is taken).
 */
export function computeWireGlow(current) {
    const absI = Math.abs(current);
    if (absI <= I_MIN)
        return 0;
    const ratio = Math.log10(absI / I_MIN) / LOG_DENOM;
    if (ratio >= 1)
        return 1;
    return ratio;
}
//# sourceMappingURL=glow.js.map